import emeraldStageSrc from 'assets/images/headlinePromo/emerald-stage.webp';
import festivalGateSrc from 'assets/images/headlinePromo/festival-gate.webp';
import goldenReelsSrc from 'assets/images/headlinePromo/golden-reels.webp';
import grandTreasureSrc from 'assets/images/headlinePromo/grand-treasure.webp';
import heroLevel1Bg from 'assets/images/headlinePromo/hero-level-1-bg.webp';
import heroLevel1CharsDesk from 'assets/images/headlinePromo/hero-level-1-chars-desk.webp';
import heroLevel1CharsMob from 'assets/images/headlinePromo/hero-level-1-chars-mob.webp';
import heroLevel2Bg from 'assets/images/headlinePromo/hero-level-2-bg.webp';
import heroLevel2CharsDesk from 'assets/images/headlinePromo/hero-level-2-chars-desk.webp';
import heroLevel2CharsMob from 'assets/images/headlinePromo/hero-level-2-chars-mob.webp';
import heroLevel3Bg from 'assets/images/headlinePromo/hero-level-3-bg.webp';
import heroLevel3CharsDesk from 'assets/images/headlinePromo/hero-level-3-chars-desk.webp';
import heroLevel3CharsMob from 'assets/images/headlinePromo/hero-level-3-chars-mob.webp';
import heroLevel4Bg from 'assets/images/headlinePromo/hero-level-4-bg.webp';
import heroLevel4CharsDesk from 'assets/images/headlinePromo/hero-level-4-chars-desk.webp';
import heroLevel4CharsMob from 'assets/images/headlinePromo/hero-level-4-chars-mob.webp';
import heroLevel5Bg from 'assets/images/headlinePromo/hero-level-5-bg.webp';
import heroLevel5CharsDesk from 'assets/images/headlinePromo/hero-level-5-chars-desk.webp';
import heroLevel5CharsMob from 'assets/images/headlinePromo/hero-level-5-chars-mob.webp';
import heroLevel6Bg from 'assets/images/headlinePromo/hero-level-6-bg.webp';
import heroLevel6CharsDesk from 'assets/images/headlinePromo/hero-level-6-chars-desk.webp';
import heroLevel6CharsMob from 'assets/images/headlinePromo/hero-level-6-chars-mob.webp';
import heroLevel7Bg from 'assets/images/headlinePromo/hero-level-7-bg.webp';
import heroLevel7CharsDesk from 'assets/images/headlinePromo/hero-level-7-chars-desk.webp';
import heroLevel7CharsMob from 'assets/images/headlinePromo/hero-level-7-chars-mob.webp';
import luckyCharmSrc from 'assets/images/headlinePromo/lucky-charm.webp';
import owlLanternSrc from 'assets/images/headlinePromo/owl-lantern.webp';
import royalVaultSrc from 'assets/images/headlinePromo/royal-vault.webp';
import type { Breakpoint } from 'components/progressBars/BreakpointsProgressBar';
import { bonusRestrictedGroups, USER_GROUP } from 'helpers/user';
import type { ILevelConfig } from '../types';
import { ELevelKey, EProgressStep, ERewardMode } from '../types';

export const HEADLINE_PROMO_NS = 'headlinePromo';

/**
 * The two families differ on purpose: the platform keeps this promo's status
 * groups as lucky7_{level}_{progress} and its reward bonuses as
 * lucky_moon_l{n}_reward.
 */
export const STATUS_GROUP_PREFIX = 'lucky7';

export const REWARD_GROUP_KEY_PATTERN = /^lucky_moon_l(\d+)_reward$/;

export const BONUSES_URL = '/bonuses?tab=active-offers';
export const GAMES_URL = '/';

export const PROGRESS_STEPS: readonly EProgressStep[] = [
  EProgressStep.START,
  EProgressStep.QUARTER,
  EProgressStep.HALF,
  EProgressStep.THREE_QUARTERS,
  EProgressStep.COMPLETE,
];

// 25/50/75% thresholds are evenly spread, so they are derived — see getLevelThreshold
export const LEVELS: readonly ILevelConfig[] = [
  {
    key: ELevelKey.CHARM,
    order: 1,
    startAmount: 0,
    targetAmount: 100,
    rewardMode: ERewardMode.AUTO,
    artifact: luckyCharmSrc,
    heroBg: heroLevel1Bg,
    heroCharsDesk: heroLevel1CharsDesk,
    heroCharsMob: heroLevel1CharsMob,
  },
  {
    key: ELevelKey.MOON_GEM,
    order: 2,
    startAmount: 100,
    targetAmount: 500,
    rewardMode: ERewardMode.AUTO,
    artifact: owlLanternSrc,
    heroBg: heroLevel2Bg,
    heroCharsDesk: heroLevel2CharsDesk,
    heroCharsMob: heroLevel2CharsMob,
  },
  {
    key: ELevelKey.GOLD_ACORN,
    order: 3,
    startAmount: 500,
    targetAmount: 2000,
    rewardMode: ERewardMode.AUTO,
    artifact: goldenReelsSrc,
    heroBg: heroLevel3Bg,
    heroCharsDesk: heroLevel3CharsDesk,
    heroCharsMob: heroLevel3CharsMob,
  },
  {
    key: ELevelKey.MAGIC_GATE,
    order: 4,
    startAmount: 2000,
    targetAmount: 8000,
    rewardMode: ERewardMode.AUTO,
    artifact: festivalGateSrc,
    heroBg: heroLevel4Bg,
    heroCharsDesk: heroLevel4CharsDesk,
    heroCharsMob: heroLevel4CharsMob,
  },
  {
    key: ELevelKey.EMERALD,
    order: 5,
    startAmount: 8000,
    targetAmount: 15_000,
    rewardMode: ERewardMode.AUTO,
    artifact: emeraldStageSrc,
    heroBg: heroLevel5Bg,
    heroCharsDesk: heroLevel5CharsDesk,
    heroCharsMob: heroLevel5CharsMob,
  },
  {
    key: ELevelKey.RAINBOW_VAULT,
    order: 6,
    startAmount: 15_000,
    targetAmount: 30_000,
    rewardMode: ERewardMode.LOOTBOX,
    artifact: royalVaultSrc,
    heroBg: heroLevel6Bg,
    heroCharsDesk: heroLevel6CharsDesk,
    heroCharsMob: heroLevel6CharsMob,
  },
  {
    key: ELevelKey.ELF_TREASURE,
    order: 7,
    startAmount: 30_000,
    targetAmount: 60_000,
    rewardMode: ERewardMode.LOOTBOX,
    artifact: grandTreasureSrc,
    heroBg: heroLevel7Bg,
    heroCharsDesk: heroLevel7CharsDesk,
    heroCharsMob: heroLevel7CharsMob,
  },
];

// Reward variants offered in the selection pop-up of L6-L7
export const DEFAULT_STATUS_GROUP = {
  levelKey: ELevelKey.CHARM,
  step: EProgressStep.START,
} as const;

export const PROGRESS_BREAKPOINTS: Breakpoint[] = PROGRESS_STEPS.map(
  (percentage) => ({ percentage, title: `${percentage}` })
);

export const RESTRICTED_GROUPS = [
  ...bonusRestrictedGroups,
  USER_GROUP.RISK_SEGMENT_S1,
  USER_GROUP.RISK_SEGMENT_S2,
];
